import { isReviewCompleted } from "~vue/Moments/composables/useReviewGroupStatus";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";

import type { ProgressStatus } from "./ProgressIcon.vue";

export type TaskActionKind = "start" | "resume" | "started" | "view" | "marked-complete" | "unavailable";

export interface TaskActionContext {
    // Hashid of the coaching session the picker is rendered inside: its own task is "started".
    currentConversationHashid: string;
    // Expired cycles allow View/Resume (plain navigation) but no new starts.
    startDisabled: boolean;
}

type Translate = (key: string) => string;

// Mirrors the PM page's CTA copy (PerformanceManagementCTA.buttonState) so a task reads the same
// wherever it appears: Start / Continue / View / Marked complete / Unavailable.
export function taskAction(review: DecoratedPerformanceReview, context: TaskActionContext, t: Translate): { kind: TaskActionKind; text: string } {
    const conversation = review.conversation;
    if (conversation?.coaching_session_hashid && conversation.coaching_session_hashid === context.currentConversationHashid) {
        return { kind: "started", text: t("chat.performancePicker.status.started") };
    }
    if (isReviewCompleted(review)) {
        // Manually completed with no conversation to open: inert, as on the PM page.
        return conversation
            ? { kind: "view", text: t("chat.performancePicker.status.view") }
            : { kind: "marked-complete", text: t("chat.performancePicker.status.markedComplete") };
    }
    if (conversation?.state === "in_progress") {
        return { kind: "resume", text: t("chat.performancePicker.status.continue") };
    }
    if (conversation) {
        return { kind: "view", text: t("chat.performancePicker.status.view") };
    }
    if (context.startDisabled) {
        return { kind: "unavailable", text: t("chat.performancePicker.status.unavailable") };
    }
    return { kind: "start", text: t("chat.performancePicker.status.start") };
}

export function taskProgress(review: DecoratedPerformanceReview): ProgressStatus {
    if (isReviewCompleted(review)) {
        return "done";
    }
    return review.conversation ? "in-progress" : "not-started";
}
